import React, { useState, useEffect, useCallback } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Bar, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { Grid } from '@mui/material';
import PropTypes from 'prop-types';

import {
  getParams,
  getMonth,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  negativeActiveColor,
  negativeDisableColor,
  shipP,
  barThinHeight,
  mediumItem,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => parseFloat(d.WorkGeneratedAmount);
const y = (d) => d.ProjectId;

// Some source systems include the project ID at the start of ProjectName.
// Remove only an exact leading ID with a separator; preserve other numbers.
const displayProjectName = (item) => {
  const name = String(item.ProjectName || '').trim();
  const id = String(item.ProjectId || '').trim();
  if (!id || !name.startsWith(id)) return name;
  const suffix = name.slice(id.length);
  return /^(?:\s+|\s*[-–:|]\s*)/.test(suffix)
    ? suffix.replace(/^(?:\s+|\s*[-–:|]\s*)/, '')
    : name;
};

let tooltipTimeout;

const BottomChart = ({
  queryData,
  selectedYears,
  selectedMonths,
  selectedTopItems,
  selectedRightItems,
  selectedMiddleItems,
  filterName,
}) => {
  const { tooltipOpen, tooltipLeft, tooltipTop, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [data, setData] = useState([]);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [selectedIdRects, setSelectedIdRects] = useState([]);
  const [selectedNameRects, setSelectedNameRects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    prepareData();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    prepareData();
  }, [queryData, selectedYears, selectedMonths, selectedTopItems, selectedMiddleItems, filterName, selectedRightItems]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let newData = [];

    let _selectedMonths = selectedMonths.slice();
    let _selectedyears = [];
    let _selectedTypes = [];

    // filter process
    selectedTopItems.forEach((item) => {
      _selectedMonths.push(item['month']);
      if (item['year']) {
        _selectedyears.push(item['year']);
      }

      if (item['type']) {
        _selectedTypes.push(item['type']);
      }
    });

    if (_selectedyears.length === 0) _selectedyears = selectedYears;

    if (_selectedTypes.length === 0) {
      _selectedTypes = ['Existing', 'New'];
    }

    if (selectedRightItems.length !== 0) {
      _selectedTypes = selectedRightItems;
    }

    if (_selectedMonths.length === 0)
      _selectedMonths = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    // data process
    if (queryData['Bottom']) {
      queryData['Bottom'].forEach((d) => {
        if (
          (_selectedMonths.indexOf(getMonth(d.Date)) > -1 ||
            _selectedMonths[0] === undefined) &&
          _selectedyears.indexOf(d.FY) > -1 &&
          _selectedTypes.indexOf(d.ExistingOrNew) > -1 &&
          (selectedMiddleItems.length === 0 ||
            selectedMiddleItems.indexOf(d[filterName]) > -1)
        ) {
          if (!dictData[y(d)]) {
            dictData[y(d)] = {};
            dictData[y(d)]['ProjectId'] = y(d);
            dictData[y(d)]['ProjectName'] = d['ProjectName'];
            dictData[y(d)]['EmployeeInitials'] = d['EmployeeInitials'];
            dictData[y(d)]['ProjectTypeDescription'] =
              d['ProjectTypeDescription'];
            dictData[y(d)]['Date'] = d['Date'];
            dictData[y(d)]['WorkGeneratedAmount'] = [];
            dictData[y(d)]['WorkGeneratedAmount']['New'] = 0;
            dictData[y(d)]['WorkGeneratedAmount']['Existing'] = 0;
          }
          dictData[y(d)]['WorkGeneratedAmount'][d.ExistingOrNew] += x(d);
        }
      });
    }

    Object.keys(dictData).forEach((key) => {
      const item = dictData[key];
      const newAmount = item.WorkGeneratedAmount.New;
      const existingAmount = item.WorkGeneratedAmount.Existing;

      // A Work Generated chart should show projects with movement in the
      // selected period, not every zero-value project snapshot.
      if (Math.abs(newAmount) > 0.005 || Math.abs(existingAmount) > 0.005) {
        newData.push(item);
      }
    });

    setData(newData.sort(
      (a, b) =>
        a.WorkGeneratedAmount.Existing +
        a.WorkGeneratedAmount.New -
        (b.WorkGeneratedAmount.Existing + b.WorkGeneratedAmount.New),
    ));
    deSelectAll();
  }, [queryData, filterName, selectedYears, selectedMonths, selectedTopItems, selectedMiddleItems, selectedRightItems]);

  const getColor = useCallback((data, type = '') => {
    const activeColor = type === 'Existing' ? negativeActiveColor : '#1c75bc';
    const disableColor = type === 'New' ? negativeDisableColor : '#ccdce9';
    if (selectedProjects.length === 0) return activeColor;
    if (selectedProjects.indexOf(y(data)) > -1) return activeColor;
    return disableColor;
  }, [selectedProjects]);

  const handleElement = useCallback((event, element, data) => {
    let _selectedBars = [],
      _selectedIdRects = [],
      _selectedNameRects = [],
      _selectedProjects = [];
    let exist = false,
      index = NaN;

    switch (element) {
      case 'bar':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedBars.length; i++) {
            if (selectedBars[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('barActive');
            _selectedBars.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('barActive');
            _selectedBars.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
            if (selectedBar === event.target) exist = true;
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });

          if (exist && selectedBars.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('barActive');
            _selectedBars = [event.target];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [y(data)];
          }
        }

        break;

      case 'ProjectId':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedIdRects.length; i++) {
            if (selectedIdRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedIdRects.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('bkgActive');
            _selectedIdRects.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });

          if (exist && selectedIdRects.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedIdRects = [event.target];
            _selectedNameRects = [];
            _selectedProjects = [y(data)];
          }
        }

        break;

      case 'EmployeeInitials':
      case 'ProjectTypeDescription':
      case 'ProjectName':
        if (event.shiftKey) {
          _selectedBars = selectedBars.slice();
          _selectedIdRects = selectedIdRects.slice();
          _selectedNameRects = selectedNameRects.slice();
          _selectedProjects = selectedProjects.slice();

          for (let i = 0; i < selectedNameRects.length; i++) {
            if (selectedNameRects[i] === event.target) {
              index = i;
              break;
            }
          }

          if (isNaN(index)) {
            event.target.classList.add('bkgActive');
            _selectedNameRects.push(event.target);
            _selectedProjects.push(y(data));
          } else {
            event.target.classList.remove('bkgActive');
            _selectedNameRects.splice(index, 1);
            _selectedProjects.splice(index, 1);
          }
        } else {
          selectedBars.forEach((selectedBar) => {
            selectedBar.classList.remove('barActive');
          });
          selectedIdRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
          });
          selectedNameRects.forEach((rect) => {
            rect.classList.remove('bkgActive');
            if (rect === event.target) exist = true;
          });

          if (exist && selectedNameRects.length === 1) {
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [];
            _selectedProjects = [];
          } else {
            event.target.classList.add('bkgActive');
            _selectedBars = [];
            _selectedIdRects = [];
            _selectedNameRects = [event.target];
            _selectedProjects = [y(data)];
          }
        }

        break;

      default:
        break;
    }

    setSelectedBars(_selectedBars);
    setSelectedIdRects(_selectedIdRects);
    setSelectedNameRects(_selectedNameRects);
    setSelectedProjects(_selectedProjects);
  }, [selectedBars, selectedIdRects, selectedNameRects, selectedProjects]);

  const deSelectAll = useCallback(() => {
    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    selectedIdRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    selectedNameRects.forEach((rect) => {
      rect.classList.remove('bkgActive');
    });

    setSelectedBars([]);
    setSelectedIdRects([]);
    setSelectedNameRects([]);
    setSelectedProjects([]);
  }, [selectedBars, selectedIdRects, selectedNameRects]);

  const showTooltipHandler = useCallback((event, data, type, isBar = true) => {
    let tooltipData, top, left;
    
    top =
      event.nativeEvent.layerY - 100;
    left = event.nativeEvent.layerX + 200;
    tooltipData = data;
    tooltipData['isBar'] = isBar;
    tooltipData['type'] = type;
    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
    }

    showTooltip({
      tooltipData: tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);

    hideTooltip();
  }, [hoverBar, hideTooltip]);

  const count = data.length;
  const width = window.innerWidth - 16;
  const height = count * barThinHeight;
  const margin = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 12,
  };
  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  const ex = (d) => d.WorkGeneratedAmount.Existing;
  const ne = (d) => d.WorkGeneratedAmount.New;

  let maxValue = 0,
    minValue = 0;
  if (count > 0) {
    let a, b;
    data.forEach((d) => {
      a = ex(d);
      b = ne(d);
      if (a >= 0 && b >= 0) {
        maxValue = Math.max(maxValue, a + b);
        minValue = Math.min(minValue, a, b);
      } else if (a >= 0 && b < 0) {
        maxValue = Math.max(maxValue, a);
        minValue = Math.min(minValue, b);
      } else if (a < 0 && b >= 0) {
        maxValue = Math.max(maxValue, b);
        minValue = Math.min(minValue, a, b);
      } else {
        maxValue = Math.max(maxValue, b);
        minValue = Math.min(minValue, a);
      }
    });
  }

  let projectIdLengths = [],
    maxProjectIdLength = 0,
    projectNameLengths = [],
    maxProjectNameLength = 0,
    projectTypeDescriptionLengths = [],
    maxProjectTypeDescriptionLength = 0;
  data.forEach((d) => {
    projectIdLengths.push(d.ProjectId.length);
    projectNameLengths.push(displayProjectName(d).length);
    projectTypeDescriptionLengths.push(d.ProjectTypeDescription.length);
  });
  if (projectIdLengths.length > 0) {
    maxProjectIdLength = Math.max(...projectIdLengths);
    maxProjectNameLength = Math.max(...projectNameLengths);
    maxProjectTypeDescriptionLength = Math.max(
      ...projectTypeDescriptionLengths,
    );
  }

  const labelPanelWidth =
    maxProjectIdLength * 8.5 +
    maxProjectNameLength * 7.3 +
    maxProjectTypeDescriptionLength * 8 +
    30;
  const chartWidth = Math.max(xMax - labelPanelWidth - 8, 120);

  // scales
  const xScale = scaleLinear({
    domain: [minValue * 1.2, maxValue * 1.2],
    range: [0, chartWidth],
    nice: true,
  });

  const yScale = scaleBand()
    .domain(data.map((d) => y(d)))
    .rangeRound([yMax, 0])
    .padding(0.2);

  let bottomHeight = window.innerWidth > 1280 ? 36.2 : 30.9;
  
  return (
    <div>
      <div className="subtitle pdv-5">By Project</div>
      {count > 0 ? (
        <>
          <Grid
            container
            style={{
              ...shipP(bottomHeight),
              display: 'flex',
              flexWrap: 'nowrap',
              width: '100%',
              alignItems: 'flex-start',
            }}
            id="wg_bottom"
          >
            <div style={{ width: labelPanelWidth, flex: `0 0 ${labelPanelWidth}px` }}>
              {data
                .slice()
                .reverse()
                .map((d, i) => {
                  return (
                    <div key={i} className="flex">
                      <p
                        style={{
                          ...mediumItem,
                          width: maxProjectIdLength * 8.5,
                        }}
                        className="grayHover"
                        onClick={(event) =>
                          handleElement(event, 'ProjectId', d)
                        }
                      >
                        {y(d)}
                      </p>
                      <p
                        style={{
                          ...mediumItem,
                          width: maxProjectNameLength * 7.3,
                        }}
                        className="grayHover"
                        onClick={(event) =>
                          handleElement(event, 'ProjectName', d)
                        }
                      >
                        {displayProjectName(d)}
                      </p>
                      <p
                        style={{ ...mediumItem, width: 30 }}
                        className="grayHover"
                        onClick={(event) =>
                          handleElement(event, 'EmployeeInitials', d)
                        }
                      >
                        {d.EmployeeInitials}
                      </p>
                      <p
                        style={{
                          ...mediumItem,
                          width: maxProjectTypeDescriptionLength * 8,
                        }}
                        className="grayHover"
                        onClick={(event) =>
                          handleElement(
                            event,
                            'ProjectTypeDescription',
                            d,
                          )
                        }
                      >
                        {d.ProjectTypeDescription}
                      </p>
                    </div>
                  );
                })}
            </div>

            <div style={{ width: chartWidth, flex: `0 0 ${chartWidth}px` }}>
              <svg width={chartWidth} height={height}>
                <rect
                  width={chartWidth}
                  height={height}
                  fill="white"
                  stroke="#a9a9a9"
                  storkewidth="1"
                  onClick={deSelectAll}
                />
                <Group top={margin.top} left={0}>
                  {data
                    .slice()
                    .reverse()
                    .map((d, i) => {
                      const existingAmount = ex(d);
                      const newAmount = ne(d);
                      let positiveOffset = 0;
                      let negativeOffset = 0;

                      const segment = (value) => {
                        const start = value >= 0 ? positiveOffset : negativeOffset;
                        const end = start + value;
                        if (value >= 0) positiveOffset = end;
                        else negativeOffset = end;

                        const startX = xScale(start);
                        const endX = xScale(end);
                        return {
                          x: Math.min(startX, endX),
                          width: Math.abs(endX - startX),
                        };
                      };

                      const newSegment = segment(newAmount);
                      const existingSegment = segment(existingAmount);

                      const barY =
                        barThinHeight * i +
                        (yScale.paddingInner() * yScale.step()) / 2;
                      const barHeight = 20;

                      return (
                        <Group key={`Project-${y(d)}-${i}`}>
                          <Bar
                            x={newSegment.x}
                            y={barY}
                            width={newSegment.width}
                            height={barHeight}
                            fill={getColor(d, 'New')}
                            onClick={(event) => {
                              handleElement(event, 'bar', d);
                            }}
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, 'New');
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, 'New');
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          />
                          <Bar
                            x={existingSegment.x}
                            y={barY}
                            width={existingSegment.width}
                            height={barHeight}
                            fill={getColor(d, 'Existing')}
                            onClick={(event) => {
                              handleElement(event, 'bar', d);
                            }}
                            onMouseMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, 'Existing');
                            }}
                            onMouseLeave={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                            onTouchMove={(event) => {
                              if (tooltipTimeout)
                                clearTimeout(tooltipTimeout);
                              showTooltipHandler(event, d, 'Existing');
                            }}
                            onTouchEnd={(event) => {
                              tooltipTimeout = setTimeout(() => {
                                hideTooltipHandler();
                              }, 300);
                            }}
                          />
                        </Group>
                      );
                    })}

                  <LinePath
                    data={[
                      { x: xScale(0), y: 0 },
                      { x: xScale(0), y: yMax },
                    ]}
                    x={(d) => (d.x ? d.x : 0)}
                    y={(d) => (d.y ? d.y : 0)}
                    stroke="#555555"
                    strokeDasharray="2,1"
                  />
                </Group>
              </svg>

              {tooltipOpen && (
                <TooltipWithBounds
                 
                  top={tooltipTop + pageYOffset}
                  left={tooltipLeft}
                >
                  {tooltipData.isBar ? (
                    <div className="bkg-white color-black">
                      <div className="pdv-5">
                        <strong>
                          {y(tooltipData)}&nbsp; &nbsp;{' '}
                          {displayProjectName(tooltipData)}
                        </strong>
                      </div>
                      <div className="ft-12">
                        Type: <strong>{tooltipData['type']}</strong>
                      </div>
                      <div className="ft-12">
                        {tooltipData['type']}:{' '}
                        <strong>
                          {thousandFormat2(
                            tooltipData['WorkGeneratedAmount'][
                              tooltipData['type']
                            ],
                          )}
                        </strong>
                      </div>
                    </div>
                  ) : null}
                </TooltipWithBounds>
              )}
            </div>
          </Grid>

          <Grid
            container
            style={{ height: barThinHeight + 5, overflowY: 'auto' }}
          >
            <svg
              width={xMax}
              height={barThinHeight}
              style={{ overflow: 'visible' }}
            >
              <rect
                x={0}
                y={0}
                width={xMax}
                height={barThinHeight}
                fill="transparent"
                onClick={(event) => {
                  deSelectAll();
                }}
              />
              <AxisBottom
                scale={xScale}
                top={0}
                left={labelPanelWidth}
                hideAxisLine={true}
                stroke="black"
                numTicks={10}
                tickStroke="black"
                tickFormat={(value, index) => value / 1000}
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '0.2em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} onClick={deSelectAll}>
                    $
                    {Math.abs(formattedValue) < 1000
                      ? `${formattedValue}K`
                      : `${formattedValue / 1000}M`}
                  </text>
                )}
              />
            </svg>
          </Grid>
        </>
      ) : null}
    </div>
  );
};

BottomChart.propTypes = {
  queryData: PropTypes.object.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedMonths: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedRightItems: PropTypes.array.isRequired,
  selectedMiddleItems: PropTypes.array.isRequired,
  filterName: PropTypes.string.isRequired,
};

export default BottomChart;
